import React, { useMemo } from "react"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { Table, TableColumn } from "resha"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import {
    APICollaborationModel,
    CollaboratorStatus,
    CollaboratorRole,
    getCollaborationID
} from "../../../../../Models/API/CollaboratorModel"
import { FilterExpression } from "../../../../../Models/Client/FilterExpression"
import { reviewerColumns } from "./Columns/reviewerColumns"
import { SortOptionField } from "../../../../Common/Button/SortChooser"
import { ChangeRole } from "../../Sections/AppDetail/Collaborators/Collaborators"
import { FlatButton } from "../../../../Common/FlatButton/FlatButton"
import { ProjectRole } from "../../../../../Models/Client/ProjectRole"

interface Props {
    myEmail: string
    roles: ProjectRole[]
    items: APICollaborationModel[] | undefined
    total: number
    loading?: boolean
    filters?: FilterExpression<APICollaborationModel>[]
    sort: SortExpression<APICollaborationModel>
    isCopyProcessing: boolean
    loadMore: () => Promise<void>
    onAdd?: () => void
    toggleActive?: (id: number, status: CollaboratorStatus) => Promise<void>
    onChangeFilter?: (value: FilterExpression<APICollaborationModel>[]) => void
    onChangeSort: (value: SortExpression<APICollaborationModel>) => void
    viewPermissions: () => void
    onChangeRole?: (args: ChangeRole) => void
    onCopyEmails?: () => void
    onDelete?: (collaborationID: number) => void
}

export const CollaboratorTable: React.FC<Props> = ({
    myEmail,
    roles,
    items,
    total,
    isCopyProcessing,
    loadMore,
    toggleActive,
    onChangeSort,
    sort,
    viewPermissions,
    onAdd,
    onChangeRole,
    onCopyEmails,
    onDelete
}: Props) => {
    const sortOptions = useMemo(() => {
        const fields: SortOptionField<APICollaborationModel>[] = [
            {
                field: "invitedEmail",
                label: "Email",
                ascLabel: "Alphabetical",
                descLabel: "Reverse alphabetical"
            },
            {
                field: "collaboratorRole",
                label: "Role",
                ascLabel: "Alphabetical",
                descLabel: "Reverse alphabetical"
            },
            {
                field: "collaboratorStatus",
                label: "Activated",
                ascLabel: "",
                descLabel: ""
            }
        ]

        return { fields, applied: sort, onChange: onChangeSort }
    }, [onChangeSort, sort])

    /**
     * Change collaborator active state
     */
    const handleToggleActive = useMemo(
        () =>
            toggleActive === undefined
                ? undefined
                : (id: number, status: CollaboratorStatus) =>
                      toggleActive(id, status),
        [toggleActive]
    )
    const columns = useMemo(
        () =>
            reviewerColumns({
                myEmail,
                roles,
                handleToggleActive,
                viewPermissions,
                changeRole:
                    onChangeRole === undefined
                        ? undefined
                        : (id: number, email: string, role: CollaboratorRole) =>
                              onChangeRole({ id, email, role }), // Open change collaborator role modal
                deleteCollaborator: onDelete
            }) as TableColumn<APICollaborationModel>[],
        [
            myEmail,
            roles,
            handleToggleActive,
            viewPermissions,
            onChangeRole,
            onDelete
        ]
    )

    const extraActions = (
        <FlatButton
            className="ml-2 flex"
            onClick={onCopyEmails}
            disabled={onCopyEmails === undefined || isCopyProcessing}
        >
            {isCopyProcessing ? "Copying..." : "Copy all emails"}
        </FlatButton>
    )

    return (
        <>
            <TableHeader
                totalCount={total}
                sortOptions={sortOptions}
                extraActions={extraActions}
            />
            <div className="flex-grow flex-shrink flex-basis-0 overflow-auto -m-6 mt-0 px-6">
                <Table<APICollaborationModel>
                    columns={columns}
                    items={items}
                    itemCount={total}
                    getKey={getCollaborationID}
                    checkedItemIds={[]}
                    className="w-full h-0 min-h-0 mt-0"
                    rowClassName="mx-6"
                    loadMore={loadMore}
                    stickyHeader={true}
                    onAdd={onAdd}
                />
            </div>
        </>
    )
}
